import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { EntrySummaryModel, InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { InvoicesRepository } from '@app/data-layer/repositories';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';
import { MANUFACTURER_EXPECTED_TO_BE_CANADIAN } from '@constants/entity-validation-messages';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { GenericService } from '@services/generic.service';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { merge, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-manufacturer-details',
  templateUrl: './es-ocr-quick-fix-manufacturer-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-manufacturer-details.component.scss'],
})
export class EsOcrQuickFixManufacturerDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public manufacturerFields: ISectionFormField[];

  @Input() public shipmentId: number;
  @Input() public invoiceId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public shipment: ShipmentModel;
  public invoice: InvoiceModel;
  public manufacturerDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public isSellerLinkReset: boolean = false;
  public seller: EntityModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private invoicesRepository: InvoicesRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    const entry = this.esOcrQuickFixService.getEntrySummary();
    this.syncEntry(entry);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.manufacturerFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const manufacturerInvalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, enumEntityModelCode.MANUFACTURER);
    if (isNeedCheck && Object.keys(manufacturerInvalidFields).length > 0) {
      this.fh.markAsTouched(this.formGroup);
      this.openMissingFieldsModal(manufacturerInvalidFields);
      return;
    }

    this.updateOrCreateManufacturer()
      .pipe(takeUntil(this.destroy$))
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public get isShowManufacturerState(): boolean {
    const country = this.formGroup.get(['address', 'country']).value;
    return country && [CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(country.code);
  }

  public get isManufacturerExpectedFromCanada(): boolean {
    return (
      !!this.invoice?.validationMessages?.some((m) => m.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN) ||
      !!this.invoice?.manufacturer?.validationMessages?.some((m) => m.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN)
    );
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const manufacturer = this.invoice?.manufacturer;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const manufacturerAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(manufacturer?.address);
    const nameField = this.manufacturerFields.find((field) => field.name === 'name');

    this.manufacturerDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: this.invoice?.manufacturer?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: manufacturerAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const manufacturerAddressErrors = this.invoice?.manufacturer?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(manufacturerAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateManufacturer() {
    const manufacturer: EntityModel = this.formGroup.value;
    const manufacturerId = this.formGroup.get('id').value;

    let newInvoiceData: Partial<InvoiceModel>;

    if (manufacturerId === this.seller?.id) {
      newInvoiceData = {
        ...this.invoice,
        manufacturer: this.seller,
        manufacturerId: this.seller?.id,
      };
    } else {
      newInvoiceData = {
        ...this.invoice,
        manufacturer,
        manufacturerId,
      };
    }

    return of(manufacturer).pipe(
      switchMap((entity) => {
        if (!manufacturerId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.MANUFACTURER;
          return this.entrySummaryStateService.createEntity(entity);
        } else {
          return this.entrySummaryStateService.updateEntity(manufacturerId, entity);
        }
      }),
      switchMap((entity) => {
        return this.updateInvoice({
          ...newInvoiceData,
          manufacturer: entity,
          manufacturerId: entity.id,
        });
      }),
      switchMap((newInvoice) => {
        const invoices = this.shipment.invoices.map((invoice) => {
          if (invoice.id === this.invoiceId) {
            return newInvoice;
          }

          return invoice;
        });

        const updatedShipment = this.shipment;
        updatedShipment.invoices = invoices;

        return this.entrySummaryStateService.updateShipment(this.shipment, updatedShipment);
      }),
      switchMap((shipment) => {
        const shipments = this.entrySummary.shipments.map((sh) => {
          if (sh.id === this.shipmentId) {
            return shipment;
          }

          return sh;
        });

        return this.entrySummaryStateService.updateEntrySummary({
          ...this.entrySummary,
          shipments,
        });
      }),
      switchMap((entry) => this.entrySummaryStateService.loadEntrySummary(entry.referenceNumber)),
      tap((entry) => {
        this.syncEntry(entry);
      }),
    );
  }

  private createNewInvoice() {
    const newInvoiceData = {
      ...this.formGroup.value,
      manufacturerId: this.formGroup.value?.manufacturer?.id,
    };

    return this.invoicesRepository.create(this.shipmentId, newInvoiceData);
  }

  private updateInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoice };
    }

    return this.invoicesRepository.update(this.invoice.id, newInvoiceData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.MANUFACTURER);

    this.formGroup.patchValue(this.invoice?.manufacturer);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((manufacturerId) => {
        if (this.isSellerLinkReset) {
          this.isSellerLinkReset = false;
        } else {
          this.applySellerOrManufacturer(manufacturerId);
        }
      });

    merge(this.formGroup.get('name').valueChanges, this.formGroup.get('address').valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        if (this.seller && this.formGroup.get('id').value === this.seller.id) {
          this.isSellerLinkReset = true;
          this.formGroup.get('id').patchValue(this.invoice?.manufacturer?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private applySellerOrManufacturer(id: number) {
    if (this.seller && id === this.seller.id) {
      this.fh.patchIsfEntity(this.formGroup, this.seller, { emitEvent: false });
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.invoice?.manufacturer);
    }
  }

  private syncEntry(entry: EntrySummaryModel) {
    this.entrySummary = entry;
    this.shipment = entry?.shipments.find((shipment) => shipment.id === this.shipmentId);
    this.invoice = this.shipment.invoices.find((invoice) => invoice.id === this.invoiceId);
    this.seller = this.shipment?.seller;
  }
}
